import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const HSA_REIMBURSEMENT_FIELD_IDS = [
  "firstName",
  "lastName",
  "middleInitial",
  "suffix",
  "gender",
  "dob",
  "street",
  "city",
  "state",
  "zip",
  "phone",
  "email",
  "employerName",
  "employerId",
  "employerLocation",
  "department",
  "ssn",
  "memberId",
  "accountNumber",
  "groupNumber",
  "planId",
  "fsaControlNumber",
  "planYearStartDate",
] as const;

export type HsaReimbursementFieldId =
  (typeof HSA_REIMBURSEMENT_FIELD_IDS)[number];

export const hsaReimbursementFieldId = createEnum(HSA_REIMBURSEMENT_FIELD_IDS);

export type HsaReimbursementSsnCollect = "full" | "last4";

export const hsaReimbursementSsnCollect = z.enum(["full", "last4"]);

const DEFAULT_FIELD_LABELS: Record<HsaReimbursementFieldId, string> = {
  firstName: "First Name",
  lastName: "Last Name",
  middleInitial: "Middle Initial",
  suffix: "Suffix",
  gender: "Gender",
  dob: "DOB",
  street: "Street Address",
  city: "City",
  state: "State",
  zip: "Zip",
  phone: "Phone",
  email: "Email",
  employerName: "Employer Name",
  employerId: "Employer ID",
  employerLocation: "Employer Location",
  department: "Department",
  ssn: "SSN",
  memberId: "Member ID",
  accountNumber: "Account Number",
  groupNumber: "Group Number",
  planId: "Plan ID",
  fsaControlNumber: "FSA Control Number",
  planYearStartDate: "Plan Year Start Date",
};

export function defaultFieldLabel(
  id: HsaReimbursementFieldId,
  collect?: HsaReimbursementSsnCollect,
): string {
  if (id === "ssn" && collect === "last4") {
    return "Last 4 digits of SSN";
  }
  return DEFAULT_FIELD_LABELS[id];
}

export type HsaReimbursementProviderField =
  | { id: "ssn"; label?: string; collect: HsaReimbursementSsnCollect }
  | {
      id: Exclude<HsaReimbursementFieldId, "ssn">;
      label?: string;
      collect?: undefined;
    };

export interface HsaReimbursementProviderForm {
  fields: HsaReimbursementProviderField[];
}

export interface HsaReimbursementProviderDirectoryEntry {
  hsa: HsaReimbursementProviderForm | null;
  fsa: HsaReimbursementProviderForm | null;
}

export const hsaReimbursementFieldSpec = z.object({
  id: hsaReimbursementFieldId,
  label: z.string(),
  collect: hsaReimbursementSsnCollect.optional(),
});

export type HsaReimbursementFieldSpec = z.infer<
  typeof hsaReimbursementFieldSpec
>;

export function resolveCatalogField(
  field: HsaReimbursementProviderField,
): HsaReimbursementFieldSpec {
  return {
    id: field.id,
    label: field.label ?? defaultFieldLabel(field.id, field.collect),
    collect: field.id === "ssn" ? field.collect : undefined,
  };
}

export const hsaReimbursementDetails = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    middleInitial: z.string().optional(),
    suffix: z.string().optional(),
    gender: z.string().optional(),
    dob: z.string().optional(),
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),
    employerName: z.string().optional(),
    employerId: z.string().optional(),
    employerLocation: z.string().optional(),
    department: z.string().optional(),
    ssn: z.string().optional(),
    memberId: z.string().optional(),
    accountNumber: z.string().optional(),
    groupNumber: z.string().optional(),
    planId: z.string().optional(),
    fsaControlNumber: z.string().optional(),
    planYearStartDate: z.string().optional(),
  })
  .strict();

export type HsaReimbursementDetails = z.infer<typeof hsaReimbursementDetails>;

const FIELD_ID_SET = new Set<string>(HSA_REIMBURSEMENT_FIELD_IDS);

export function isHsaReimbursementFieldId(
  value: string,
): value is HsaReimbursementFieldId {
  return FIELD_ID_SET.has(value);
}

export const DERIVED_DETAIL_SOURCES = [
  "ssnLast4",
  "ssnDigit.0",
  "ssnDigit.1",
  "ssnDigit.2",
  "ssnDigit.3",
  "ssnDigit.4",
  "ssnDigit.5",
  "ssnDigit.6",
  "ssnDigit.7",
  "ssnDigit.8",
  "dobMM",
  "dobDD",
  "dobYY",
  "dobYYYY",
  "dobMMDDYYYY",
] as const;

export type DerivedDetailSource = (typeof DERIVED_DETAIL_SOURCES)[number];

const DERIVED_DETAIL_SOURCE_SET = new Set<string>(DERIVED_DETAIL_SOURCES);

export function isDerivedDetailSource(
  value: string,
): value is DerivedDetailSource {
  return DERIVED_DETAIL_SOURCE_SET.has(value);
}

export function detailValue(
  details: HsaReimbursementDetails,
  id: HsaReimbursementFieldId,
): string {
  return details[id] ?? "";
}
